"use client";

import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";

interface CtaBannerSectionProps {
  section: VisualCmsSection;
}

function readString(c: Record<string, unknown>, key: string): string {
  const v = c[key];
  return typeof v === "string" ? v : "";
}

export function CtaBannerSection({ section }: CtaBannerSectionProps) {
  const content = (section.content as Record<string, unknown>) ?? {};
  const title = readString(content, "title");
  const subtitle = readString(content, "subtitle");
  const ctaText = readString(content, "ctaText");
  const ctaLink = readString(content, "ctaLink");
  const secondaryCtaText = readString(content, "secondaryCtaText");
  const secondaryCtaLink = readString(content, "secondaryCtaLink");
  const shell = sectionShellStyle(section);

  const fallbackBg = !shell.style.backgroundColor;

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        <div
          className={`rounded-2xl px-6 sm:px-10 py-10 sm:py-14 text-center ${
            fallbackBg ? "bg-green-600 text-white" : ""
          }`}
        >
          {title && (
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="mt-3 text-base sm:text-lg max-w-2xl mx-auto opacity-90 whitespace-pre-wrap">
              {subtitle}
            </p>
          )}
          {(ctaText || secondaryCtaText) && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {ctaText && (
                <a
                  href={ctaLink || "#"}
                  className={`inline-flex items-center justify-center rounded-md px-5 py-3 text-sm font-semibold shadow-sm ${
                    fallbackBg
                      ? "bg-white text-green-700 hover:bg-gray-100"
                      : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  {ctaText}
                </a>
              )}
              {secondaryCtaText && (
                <a
                  href={secondaryCtaLink || "#"}
                  className="inline-flex items-center justify-center rounded-md border border-current/30 px-5 py-3 text-sm font-semibold hover:bg-current/10"
                >
                  {secondaryCtaText}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
